import React, { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";

import Spacer from "../../Spacer";
import LungIcon from "../../icons/LungIcon";
import StackedLineChartIcon from "../../icons/StackedLineChartIcon";
import PoincareChart from "../../charts/PoincareChart";
import PostExertionDecayChart from "../../charts/PostExertionDecayChart";
import PostExertionRespiratorySignalChart from "../../charts/PostExertionRespiratorySignalChart";
import MetricValue from "../MetricValue";
import {
  formatRecordingIdDateParts,
  parseTimestampFromRecordingId,
} from "../../../utils/results/recordingIdTime";
import {
  ResultSummaryRow,
  ResultSummarySection,
} from "../ResultSummarySection";
import PostExertionRespiratorySummary from "../PostExertionRespiratorySummary";
import { trackEvent } from "../../../utils/analytics/track";
import RecordingQualitySection from "../RecordingQualitySection";
import {
  pickPostExertionRecoveryMetrics,
  pickPostExertionDecaySeries,
  pickPostExertionRrMax,
  pickPostExertionRrMin,
  pickPostExertionSignal,
} from "../../../utils/results/postExertion";
import { pickRespiratoryPoincare } from "../../../utils/results/respiratoryPoincare";
import { useUserWebAppPresentationFeatures } from "../../../hooks/useUserWebAppPresentationFeatures";
import ResultViewScaffold from "../ResultViewScaffold";
import ResultCard from "../../common/ResultCard";
import AveragedFlowLoopsSection from "../AveragedFlowLoopsSection";
import {
  getResultModeIcon,
  getResultModeLabel,
} from "../../../utils/results/resultModePresentation";
import {
  RECORDING_QUALITY_CHECK_STATES,
  RECORDING_QUALITY_OVERALL_STATES,
  buildRecordingQualityCheck,
  getRecordingQualitySummaryCopy,
  summarizeRecordingQualityChecks,
} from "../../../utils/results/recordingQuality";
import {
  formatRecoveryDuration,
  formatRecoveryPercent,
  getBreathingVariabilityAxisMax,
} from "../../../utils/results/postExertionView";
import {
  pickRr,
  pickRrSignalQuality,
} from "../../../utils/results/signalQuality";
import {
  pickAveragedFlowLoop,
  pickPrimaryFlowLoopRecord,
} from "../../../utils/results/flowLoops";
import { readFiniteNumber } from "../../../utils/results/viewTimeSeries";
import PostExertionCumulativePeakHeightChart from "../../charts/PostExertionCumulativePeakHeightChart";

function PostExertionResultViewImpl({
  data,
  docId,
  variant,
  repeatLinkTo = "",
  footer = null,
}) {
  const { t, i18n } = useTranslation();
  const {
    showFullResults,
    resultFeatures = {},
    elementFeatures = {},
  } = useUserWebAppPresentationFeatures();
  const showSignalQuality = Boolean(resultFeatures.signalQuality);
  const showRr = Boolean(resultFeatures.rr);
  const showFlowLoops = Boolean(resultFeatures.flowLoops);
  const showPoincareChart = Boolean(elementFeatures.poincareChart);
  const showRespiratorySignalChart = Boolean(
    elementFeatures.respiratorySignalChart,
  );
  const rr = useMemo(() => pickRr(data), [data]);
  const rrMin = useMemo(() => pickPostExertionRrMin(data), [data]);
  const rrMax = useMemo(() => pickPostExertionRrMax(data), [data]);
  const rrSignalQuality = useMemo(() => pickRrSignalQuality(data), [data]);
  const recordingTimestamp = useMemo(
    () => parseTimestampFromRecordingId(docId),
    [docId],
  );
  const recoveryMetrics = useMemo(
    () => pickPostExertionRecoveryMetrics(data),
    [data],
  );
  const decaySeries = useMemo(() => pickPostExertionDecaySeries(data), [data]);
  const respiratorySignal = useMemo(() => pickPostExertionSignal(data), [data]);
  const respiratoryPoincare = useMemo(
    () => pickRespiratoryPoincare(data),
    [data],
  );
  const respiratoryPoincareAxisMax = useMemo(
    () => getBreathingVariabilityAxisMax(respiratoryPoincare.maxInterval),
    [respiratoryPoincare.maxInterval],
  );
  const flowLoopRecord = useMemo(() => pickPrimaryFlowLoopRecord(data), [data]);
  const averagedFlowLoop = useMemo(() => pickAveragedFlowLoop(data), [data]);
  const recordingQualitySummary = useMemo(
    () =>
      summarizeRecordingQualityChecks([
        showSignalQuality
          ? buildRecordingQualityCheck({
              key: "respiratory",
              label: t(
                "result.postExertion.respiratorySignalQuality",
                "Respiratory Signal Quality",
              ),
              rawStatus: rrSignalQuality,
              recordingTimestamp,
              message:
                rrSignalQuality != null
                  ? t(
                      "result.resp.qualityFailTitle",
                      "Low respiratory signal quality.",
                    )
                  : null,
            })
          : null,
      ]),
    [recordingTimestamp, rrSignalQuality, showSignalQuality, t],
  );
  const respiratoryQualityStatus =
    recordingQualitySummary.checks.find((check) => check.key === "respiratory")
      ?.state || RECORDING_QUALITY_CHECK_STATES.UNAVAILABLE;
  const respiratoryQualityIsFailing =
    respiratoryQualityStatus === RECORDING_QUALITY_CHECK_STATES.FAIL;
  const rrUnit = t("result.resp.rrUnit", "bpm");
  const recovery60Unit =
    readFiniteNumber(recoveryMetrics.recoveryFraction60) != null ? "%" : null;
  const recovery120Unit =
    readFiniteNumber(recoveryMetrics.recoveryFraction120) != null ? "%" : null;
  const respiratorySummaryRows = useMemo(
    () => [
      {
        leadingLabel: t("result.resp.rrShortLabel", "RR"),
        metrics: [
          {
            label: t("result.postExertion.rrSummaryAvgLabel", "Avg"),
            value: (
              <MetricValue
                metric="rr"
                value={rr}
                unit={rrUnit}
                recordingTimestamp={recordingTimestamp}
              />
            ),
          },
          {
            label: t("result.postExertion.rrSummaryMinLabel", "Min"),
            value: (
              <MetricValue
                metric="rr"
                value={rrMin}
                unit={rrUnit}
                recordingTimestamp={recordingTimestamp}
              />
            ),
          },
          {
            label: t("result.postExertion.rrSummaryMaxLabel", "Max"),
            value: (
              <MetricValue
                metric="rr"
                value={rrMax}
                unit={rrUnit}
                recordingTimestamp={recordingTimestamp}
              />
            ),
          },
        ],
      },
    ],
    [rr, rrMin, rrMax, rrUnit, recordingTimestamp, t],
  );
  const recoverySummaryRows = useMemo(
    () => [
      {
        leadingLabel: t("result.resp.rrShortLabel", "RR"),
        metrics: [
          {
            label: t("result.postExertion.summary60SecondsLabel", "60s"),
            value: (
              <MetricValue
                metric="rr"
                value={recoveryMetrics.rrRecovery60}
                unit={rrUnit}
                recordingTimestamp={recordingTimestamp}
              />
            ),
          },
          {
            label: t("result.postExertion.summary120SecondsLabel", "120s"),
            value: (
              <MetricValue
                metric="rr"
                value={recoveryMetrics.rrRecovery120}
                unit={rrUnit}
                recordingTimestamp={recordingTimestamp}
              />
            ),
          },
        ],
      },
      {
        leadingLabel: t("result.postExertion.recoveryTitle", "Recovery"),
        metrics: [
          {
            label: t("result.postExertion.summary60SecondsLabel", "60s"),
            value: formatRecoveryPercent(recoveryMetrics.recoveryFraction60),
            unit: recovery60Unit,
          },
          {
            label: t("result.postExertion.summary120SecondsLabel", "120s"),
            value: formatRecoveryPercent(recoveryMetrics.recoveryFraction120),
            unit: recovery120Unit,
          },
        ],
      },
      {
        leadingLabel: t("result.postExertion.summaryTimeLabel", "Time"),
        metrics: [
          {
            label: t("result.postExertion.summary95PercentLabel", "95%"),
            value: formatRecoveryDuration(recoveryMetrics.timeToRecovery95),
          },
          {
            label: t("result.postExertion.summary90PercentLabel", "90%"),
            value: formatRecoveryDuration(recoveryMetrics.timeToRecovery90),
          },
        ],
      },
    ],
    [
      recovery60Unit,
      recovery120Unit,
      recoveryMetrics,
      rrUnit,
      recordingTimestamp,
      t,
    ],
  );

  const recordingTime = useMemo(
    () =>
      formatRecordingIdDateParts(
        docId,
        i18n.resolvedLanguage || i18n.language || "en-GB",
      ),
    [docId, i18n.resolvedLanguage, i18n.language],
  );
  const compactRecordingQualityStatus = useMemo(() => {
    if (!showSignalQuality) {
      return null;
    }

    if (
      recordingQualitySummary.overallState !==
        RECORDING_QUALITY_OVERALL_STATES.PASSED &&
      recordingQualitySummary.overallState !==
        RECORDING_QUALITY_OVERALL_STATES.PENDING
    ) {
      return null;
    }

    const summaryCopy = getRecordingQualitySummaryCopy(t, recordingQualitySummary);
    return summaryCopy
      ? {
          tone: summaryCopy.tone,
          text: summaryCopy.title,
          accessibleLabel: summaryCopy.accessibleLabel,
        }
      : null;
  }, [recordingQualitySummary, showSignalQuality, t]);
  const showRecordingQualitySection =
    showSignalQuality && compactRecordingQualityStatus == null;

  const showDetailPanels =
    showRespiratorySignalChart ||
    showPoincareChart ||
    showFlowLoops ||
    showFullResults;
  const details = showDetailPanels ? (
    <section className="p-4 flex flex-col gap-4 w-full h-fit">
      {showRespiratorySignalChart ? (
        <ResultCard
          icon={<LungIcon />}
          title={t("result.resp.signalChartTitle", "Respiratory Signal")}
        >
          {respiratorySignal.points.length ? (
            <PostExertionRespiratorySignalChart
              detrendedPoints={respiratorySignal.detrendedPoints}
              height={220}
              maxSecs={respiratorySignal.maxSecs}
            />
          ) : (
            <div className="muted-text">
              {t("result.resp.noSignal", "No signal trace available.")}
            </div>
          )}
        </ResultCard>
      ) : null}

      {showFullResults ? (
        <ResultCard
          icon={getResultModeIcon(variant)}
          title={t("result.postExertion.decayChartTitle", "Recovery Decay")}
        >
          {decaySeries.points.length ? (
            <PostExertionDecayChart
              points={decaySeries.points}
              expR2={decaySeries.expR2}
              linearR2={decaySeries.linearR2}
              height={260}
            />
          ) : (
            <div className="muted-text">
              {t(
                "result.postExertion.noDecayData",
                "No recovery decay data available.",
              )}
            </div>
          )}
        </ResultCard>
      ) : null}

      {showPoincareChart ? (
        <ResultCard
          icon={<LungIcon />}
          title={t(
            "result.resp.breathingVariabilityPoincareTitle",
            "Breathing Variability Poincare",
          )}
        >
          {respiratoryPoincare.x.length && respiratoryPoincare.y.length ? (
            <PoincareChart
              x={respiratoryPoincare.x}
              y={respiratoryPoincare.y}
              height={280}
              initialMode="basic"
              axisMin={0}
              axisMax={respiratoryPoincareAxisMax}
              xTitle={t("result.resp.breathingVariabilityXAxis", "IBI_n (s)")}
              yTitle={t("result.resp.breathingVariabilityYAxis", "IBI_n+1 (s)")}
            />
          ) : (
            <div className="muted-text">
              {t(
                "result.resp.noBreathingVariabilityPoincare",
                "No breathing variability Poincare data available.",
              )}
            </div>
          )}
        </ResultCard>
      ) : null}

      {showFullResults ? (
        <ResultCard
          icon={getResultModeIcon(variant)}
          title={t(
            "result.postExertion.cumulativePeakHeightChartTitle",
            "Cumulative Peak Height",
          )}
        >
          {respiratorySignal.detrendedPoints.length ? (
            <PostExertionCumulativePeakHeightChart
              detrendedPoints={respiratorySignal.detrendedPoints}
              height={240}
              maxSecs={respiratorySignal.maxSecs}
            />
          ) : (
            <div className="muted-text">
              {t(
                "result.postExertion.noCumulativePeakHeightData",
                "No cumulative peak height data available.",
              )}
            </div>
          )}
        </ResultCard>
      ) : null}

      {showFlowLoops && averagedFlowLoop ? (
        <ResultCard
          icon={<LungIcon />}
          title={t(
            "results.flowLoops.rollingRecoverySectionTitle",
            "Rolling recovery flow-volume loops",
          )}
        >
          <AveragedFlowLoopsSection flowLoopSource={flowLoopRecord} />
        </ResultCard>
      ) : null}
    </section>
  ) : null;

  return (
    <ResultViewScaffold
      title={getResultModeLabel(variant, t)}
      icon={getResultModeIcon(variant)}
      recordingTime={recordingTime}
      statusLine={compactRecordingQualityStatus}
      details={details}
      footer={footer}
    >
      <Spacer />

      {showRecordingQualitySection ? (
        <RecordingQualitySection
          summary={recordingQualitySummary}
          guidanceLinkTo="/guidance/cardiopulmonary"
          guidanceCtaLabel={t("result.cardiopulmonary.guidanceCta", "Tips")}
          repeatLinkTo={repeatLinkTo}
          repeatCtaLabel={t(
            "result.recordingQuality.repeatCta",
            "Repeat",
          )}
          onGuidanceClick={() =>
            trackEvent("post_exertion_result_click_guidance", {
              respiratory_quality_failed: respiratoryQualityIsFailing,
            })
          }
        />
      ) : null}

      {showRr ? (
        <PostExertionRespiratorySummary
          title={t("result.tidal.respiratory", "Respiratory")}
          rows={respiratorySummaryRows}
          disabled={respiratoryQualityIsFailing}
        />
      ) : null}

      {showFullResults ? (
        <PostExertionRespiratorySummary
          icon={<StackedLineChartIcon />}
          title={t("result.postExertion.recoveryTitle", "Recovery")}
          rows={recoverySummaryRows}
        />
      ) : null}
    </ResultViewScaffold>
  );
}

const PostExertionResultView = memo(PostExertionResultViewImpl);

PostExertionResultView.isReady = (data) => pickRr(data) != null;

export default PostExertionResultView;
